import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  BarChart3, Users, Bot, RefreshCw, Loader2, ArrowLeft, Phone, Globe,
} from "lucide-react";
import { toast } from "sonner";
import { ADMIN_TOKEN_KEY, adminVisite } from "../lib/adminApi";

export default function PlanciaVisite() {
  const navigate = useNavigate();
  const [token] = useState(() => localStorage.getItem(ADMIN_TOKEN_KEY));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!token) { navigate("/plancia"); return; }
    setLoading(true);
    try {
      const d = await adminVisite(token);
      setData(d);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Sessione scaduta — rientra");
        localStorage.removeItem(ADMIN_TOKEN_KEY);
        navigate("/plancia");
      } else {
        toast.error("Errore nel caricamento visite");
      }
    } finally { setLoading(false); }
  }, [token, navigate]);

  useEffect(() => { load(); }, [load]);

  const r = data?.riepilogo || {};
  const giorni = data?.per_giorno || [];
  const maxDay = Math.max(1, ...giorni.map((g) => g.umani + g.bot));

  return (
    <div className="min-h-screen bg-brand-bg text-brand-ink">
      <div className="max-w-5xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
          <div>
            <button onClick={() => navigate("/plancia")}
              className="text-sm text-brand-inkSoft hover:text-brand-ink flex items-center gap-1 mb-2">
              <ArrowLeft className="w-4 h-4" /> Plancia
            </button>
            <h1 className="font-heading text-2xl md:text-3xl text-brand-ink flex items-center gap-2">
              <BarChart3 className="w-6 h-6 text-brand-terra" /> Visite al sito
            </h1>
            <p className="text-sm text-brand-inkSoft">Chi passa da 24vox.it — persone vere separate dai robot.</p>
          </div>
          <button onClick={load} className="p-2 rounded-lg border border-brand-line bg-white hover:bg-brand-bg2" title="Aggiorna">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>

        {loading && !data ? (
          <div className="flex items-center justify-center py-20 text-brand-inkSoft">
            <Loader2 className="w-6 h-6 animate-spin" />
          </div>
        ) : (
          <>
            {/* Card riepilogo */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
              <Card icon={<Users className="w-5 h-5" />} label="Persone oggi" value={r.oggi_umani ?? 0} accent />
              <Card icon={<Users className="w-5 h-5" />} label="Persone (7 giorni)" value={r.settimana_umani ?? 0} accent />
              <Card icon={<Phone className="w-5 h-5" />} label="Demo provate (7 giorni)" value={r.demo_settimana ?? 0}
                danger={(r.demo_settimana ?? 0) === 0} />
              <Card icon={<Bot className="w-5 h-5" />} label="Robot (7 giorni)" value={r.settimana_bot ?? 0} muted />
            </div>

            {/* Grafico per giorno */}
            <div className="bg-white border border-brand-line rounded-2xl p-5 mb-6">
              <h2 className="font-heading text-lg mb-4">Ultimi 7 giorni</h2>
              <div className="flex items-end justify-between gap-2 h-40">
                {giorni.map((g, i) => (
                  <div key={i} className="flex-1 flex flex-col items-center gap-1 h-full justify-end">
                    <div className="w-full flex flex-col justify-end items-center h-full">
                      <div className="w-full rounded-t bg-brand-terra transition-all"
                        style={{ height: `${(g.umani / maxDay) * 100}%`, minHeight: g.umani ? "4px" : "0" }}
                        title={`${g.umani} persone`} />
                      <div className="w-full bg-brand-line/70"
                        style={{ height: `${(g.bot / maxDay) * 100}%`, minHeight: g.bot ? "3px" : "0" }}
                        title={`${g.bot} robot`} />
                    </div>
                    <span className="text-[11px] text-brand-inkSoft whitespace-nowrap">{g.giorno}</span>
                    <span className="text-xs font-semibold">{g.umani}</span>
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-4 mt-4 text-xs text-brand-inkSoft">
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-brand-terra inline-block" /> Persone</span>
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-brand-line inline-block" /> Robot</span>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {/* Pagine più viste */}
              <div className="bg-white border border-brand-line rounded-2xl p-5">
                <h2 className="font-heading text-lg mb-4 flex items-center gap-2"><Globe className="w-4 h-4" /> Pagine più viste</h2>
                {(!data?.top_pagine || data.top_pagine.length === 0) ? (
                  <p className="text-sm text-brand-inkSoft">Ancora nessuna visita di persone reali registrata.</p>
                ) : (
                  <ul className="space-y-2">
                    {data.top_pagine.map((p, i) => (
                      <li key={i} className="flex items-center justify-between text-sm border-b border-brand-line/60 pb-2 last:border-0">
                        <span className="font-mono text-brand-ink">{p.pagina === "/" ? "/ (home)" : p.pagina}</span>
                        <span className="font-semibold">{p.visite}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Visite recenti */}
              <div className="bg-white border border-brand-line rounded-2xl p-5">
                <h2 className="font-heading text-lg mb-4 flex items-center gap-2"><Users className="w-4 h-4" /> Visite recenti (persone)</h2>
                {(!data?.visite_recenti || data.visite_recenti.length === 0) ? (
                  <p className="text-sm text-brand-inkSoft">Nessuna visita di persone reali ancora.</p>
                ) : (
                  <div className="max-h-96 overflow-y-auto -mx-1">
                    <ul className="space-y-2">
                      {data.visite_recenti.map((v, i) => (
                        <li key={i} className="text-sm px-1 border-b border-brand-line/60 pb-2 last:border-0">
                          <div className="flex items-center justify-between">
                            <span className="font-medium">{v.quando}</span>
                            <span className="text-xs text-brand-inkSoft">{v.dispositivo} · {v.browser}</span>
                          </div>
                          <div className="flex items-center justify-between text-xs text-brand-inkSoft mt-0.5">
                            <span className="font-mono">{v.pagina === "/" ? "/ (home)" : v.pagina}</span>
                            <span className="font-mono">{v.ip}</span>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>

            <p className="text-xs text-brand-inkSoft mt-6 leading-relaxed">
              I robot (Google, Bing e scanner automatici) sono riconosciuti dalla loro firma e tenuti separati:
              il numero "Persone" è la stima di visitatori reali. Se "Demo provate" resta a zero, la gente guarda ma non prova ancora.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

function Card({ icon, label, value, accent, muted, danger }) {
  return (
    <div className={`rounded-2xl p-4 border ${accent ? "bg-brand-ink text-brand-bg border-brand-ink" : "bg-white border-brand-line"}`}>
      <div className={`flex items-center gap-2 text-xs uppercase tracking-wider mb-1 ${accent ? "text-brand-bg/70" : "text-brand-inkSoft"}`}>
        {icon} {label}
      </div>
      <div className={`font-heading text-3xl ${danger ? "text-brand-terra" : accent ? "text-brand-bg" : muted ? "text-brand-inkSoft" : "text-brand-ink"}`}>
        {value}
      </div>
    </div>
  );
}
